/**
 * Operational metrics — mgmt API, used on page load. Not the same
 * backend as ./analytics.ts. Keeps the legacy error contract; see types.ts.
 */

import constants from '../../constants'
import axiosInstance from '../axiosInstance'
import { toServiceError } from './errors'
import {
  MetricEnvelope,
  MetricRangeParams,
  MetricsApp,
  MetricsBundle,
  MetricsServiceError,
} from './types'

/**
 * All eight page-load metrics in parallel. Each field holds the FULL response body,
 * and voice/video/recording/transcription share one range.
 */
export async function get(
  app: MetricsApp,
  mauCall: MetricRangeParams,
  peakCcuCall: MetricRangeParams,
  voiceVideoCall: MetricRangeParams,
  mcuCall: MetricRangeParams,
  dcuCall: MetricRangeParams,
): Promise<MetricsBundle | MetricsServiceError> {
  const URL = `${constants.customerDomain}apps/${app.id}/metrics/`

  const requests = [
    `${URL}${mauCall.type}/${mauCall.startRange}/${mauCall.endRange}`,
    `${URL}${peakCcuCall.type}/${peakCcuCall.startRange}/${peakCcuCall.endRange}`,
    `${URL}voice/${voiceVideoCall.startRange}/${voiceVideoCall.endRange}`,
    `${URL}video/${voiceVideoCall.startRange}/${voiceVideoCall.endRange}`,
    `${URL}recording/${voiceVideoCall.startRange}/${voiceVideoCall.endRange}`,
    `${URL}transcription/${voiceVideoCall.startRange}/${voiceVideoCall.endRange}`,
    `${URL}${mcuCall.type}/${mcuCall.startRange}/${mcuCall.endRange}`,
    `${URL}${dcuCall.type}/${dcuCall.startRange}/${dcuCall.endRange}`,
  ].map(url => axiosInstance.get(url, { timeout: constants.timeout_in_ms.metrics }))

  const settled = await Promise.allSettled(requests)

  // Only a total failure is an error: one metric the app's region cannot serve
  // must not blank the other seven charts.
  const rejections = settled.filter(
    (outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected',
  )
  if (rejections.length === settled.length) return toServiceError(rejections[0].reason)

  // A failed call reads as "no points", which every caller already renders as empty.
  const bodyAt = <T>(index: number): MetricEnvelope<T> => {
    const outcome = settled[index]
    return outcome.status === 'fulfilled' ? outcome.value.data : { data: [] }
  }

  return {
    mau: bodyAt(0),
    peakCcu: bodyAt(1),
    voice: bodyAt(2),
    video: bodyAt(3),
    recording: bodyAt(4),
    transcription: bodyAt(5),
    mcu: bodyAt(6),
    dcu: bodyAt(7),
    metricSuccess: true,
  }
}

export default function init(): void {}
